import {
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  EventEmitter,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { EMPTY, Observable, of, Subscription } from 'rxjs';
import { catchError, delay, map, switchMap } from 'rxjs/operators';

import { getSweetAlertOptions } from '@commons/utility/util';

import { show_popup, TITLE_ERROR } from '@shared/utility/popup-error';

import { DocumentInfo } from '@core/model/document-info';
import { retryWithDelay } from '@core/operators/retry-delay';

import Swal from 'sweetalert2';
import { DocumentVerificationService } from './document-verification.service';

import { INPUT_PATTERNS_CONSTANT } from './rapi-doc-info.constant';

@Component({
  selector: 'app-rapi-doc-info',
  templateUrl: './rapi-doc-info.component.html',
  styleUrls: ['./rapi-doc-info.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => RapiDocInfoComponent),
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => RapiDocInfoComponent),
      multi: true,
    },
  ],
})
export class RapiDocInfoComponent implements OnInit, ControlValueAccessor, OnDestroy {
  @Input() disableDocNumberControl: boolean = false;
  @Output() enableFields = new EventEmitter<boolean>();
  @ViewChild('docInput', { static: false }) docInput: ElementRef;

  form: FormGroup;
  subscriptions: Subscription[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private docVerificationService: DocumentVerificationService,
    private router: Router
  ) {
    this.form = this.formBuilder.group(
      {
        docNumber: [
          '',
          [Validators.required, Validators.pattern('^[0-9]*$'), Validators.minLength(6), Validators.maxLength(12)],
        ],
      },
      {
        updateOn: 'blur',
      }
    );
    let id_detail = sessionStorage.getItem('cai');
    if (id_detail != null && id_detail != 'undefined') {
      this.form.controls['docNumber'].markAsTouched({ onlySelf: true });
      this.form.controls['docNumber'].setValidators(null);
    }

    this.subscriptions.push(
      this.form.valueChanges.subscribe((value) => {
        this.onChange(value);
        this.onTouched();
      })
    );

    this.form.controls['docNumber'].setAsyncValidators(this.asyncValidateDocPipe());
    this.form.controls['docNumber'].statusChanges.subscribe(() => {
      this.form.updateValueAndValidity();
      this.onChange(this.form.value);
    });
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  get docNumberControl() {
    return this.form?.controls.docNumber;
  }

  get value(): DocumentInfo {
    return this.form.value;
  }

  set value(value: DocumentInfo) {
    this.form.setValue(value);
    this.onChange(value);
    this.onTouched();
  }

  writeValue(value: any): void {
    if (value) {
      this.value = value;
    }

    if (value === null) {
      this.form.reset();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {}

  ngOnInit() {}

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  ngOnChanges() {
    this.docNumberControl?.updateValueAndValidity();
    if (this.disableDocNumberControl) {
      this.docNumberControl.disable();
    } else {
      this.docNumberControl.enable();
    }
  }

  validate(_: FormControl) {
    if (!this.docNumberControl.value || this.docNumberControl.invalid || this.docNumberControl.pending) {
      return { docInfo: { valid: false } };
    }
    return null;
  }

  private _showExistenceMessage(message: string) {
    let id_detail = sessionStorage.getItem('cai');
    let options = getSweetAlertOptions(
      message,
      'INICIAR SESIÓN',
      id_detail == null || id_detail === 'undefined',
      'RECTIFICAR DOCUMENTO'
    );

    (document.activeElement as HTMLElement)?.blur();
    Swal.fire(options).then((result) => {
      if (result.value) {
        this.router.navigate(['/login']);
      } else {
        this.enableFieldsEvent();
      }
    });
  }

  enableFieldsEvent() {
    this.docNumberControl.reset();
    this.enableFields.emit(false);
    setTimeout(() => this.docInput.nativeElement.focus());
  }

  private isValidPattern(docNumber: string | null): boolean {
    return docNumber ? INPUT_PATTERNS_CONSTANT.NUMBER_RANGE.test(docNumber) : false;
  }

  asyncValidateDocPipe(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!this.isValidPattern(control.value)) {
        return of({ pattern: true });
      }

      return of(control.value).pipe(
        delay(100),
        switchMap(() => {
          if (!this.docNumberControl.value || this.docNumberControl.value.length === 0) {
            return of({ required: true });
          }
          return this.docVerificationService.existsByDocTypeAndNumber(1, this.docNumberControl.value).pipe(
            map((result) => {
              if (result.response === true) {
                this._showExistenceMessage(result.message);
                return { valid: false };
              }
              return null;
            })
          );
        }),
        retryWithDelay(1000, 3),
        catchError(() => {
          show_popup(
            TITLE_ERROR,
            'Lo sentimos, ocurrió un error al validar tu documento de identidad. Inténtalo de nuevo  P-16'
          );
          this.docNumberControl.setValue(null);
          return EMPTY;
        })
      );
    };
  }

  checkIsNumber(e) {
    e = e || window.event;
    var charCode = typeof e.which == 'undefined' ? e.keyCode : e.which;
    var charStr = String.fromCharCode(charCode);

    if (!charStr.match(INPUT_PATTERNS_CONSTANT.NUMBER)) e.preventDefault();
  }
}
